import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useKeyboardLift } from '../../hooks/useKeyboardLift';
import { useScrollSnap } from '../../hooks/useScrollSnap';
import { useNavigate } from 'react-router-dom';
import { IonPage, IonContent, IonList, IonItem, IonItemSliding, IonItemOptions, IonItemOption, IonLabel, IonButton, IonIcon, IonGrid, IonSearchbar, IonFab, IonFabButton, IonAvatar, IonChip, IonText, IonSpinner, useIonRouter } from '@ionic/react';
import BasePageWithHeader, { TitleCard } from '../../components/BasePageWithHeader';
import { chevronBackOutline, homeOutline, searchOutline, closeOutline, refreshOutline, callOutline, mailOutline, swapHorizontal, peopleOutline } from 'ionicons/icons';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { StaffCacheService, type CachedStaffMember } from '@rsmaids/shared/services/api/staffCacheService';
import contactsStyles from '../bookingSystem/ContactsList.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatarUrl?: string;
}

const getStaffName = (staff: CachedStaffMember): string => {
  return staff.name || staff.email || i18n.t('pages.teamMessaging.MembersPage.staffMember');
};

const getMemberStatus = (member: TeamMember): string => {
  if (member.email) return member.email;
  if (member.phone) return member.phone;
  return i18n.t('pages.teamMessaging.MembersPage.staffMember2');
};

const MembersPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ionRouter = useIonRouter();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [search, setSearch] = useState('');
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(false);
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const titleCardRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLIonGridElement>(null);
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);
  const overlayRef = useRef<HTMLElement | null>(null);
  // Lift the floating search bar above the native keyboard (same as the other search bars)
  useKeyboardLift({ bars: () => [overlayRef.current], scroll: () => gridRef.current });

  const loadMembers = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    try {
      const staffCache = StaffCacheService.getInstance();
      await staffCache.loadStaffCache(forceRefresh);
      const staff = staffCache.getAllStaff();

      const mapped = staff
        .map((member) => {
          const email = member.email || '';
          const phone = member.phone || '';
          return {
            id: member.staffMemberId,
            name: getStaffName(member),
            email,
            phone,
            avatarUrl: proxyImage(member.contactAvatar || member.mainMedia || ''),
          } satisfies TeamMember;
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      setMembers(mapped);
    } catch (error) {
      console.error('Error loading staff members:', error);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(gridRef, 66, applyProgress);

  useEffect(() => {
    Promise.resolve().then(() => loadMembers());
  }, [loadMembers]);

  const updateOverlayPosition = useCallback(() => {
    if (!overlayRef.current) return;
    const viewportWidth = window.innerWidth;
    if (isSearchVisible) {
      const desiredWidth = viewportWidth - 32;
      overlayRef.current.style.setProperty('--overlay-w', `${desiredWidth}px`);
      overlayRef.current.style.setProperty('--overlay-left', '16px');
    } else {
      overlayRef.current.style.setProperty('--overlay-w', '56px');
      const centerLeft = (viewportWidth / 2) - 28;
      overlayRef.current.style.setProperty('--overlay-left', `${centerLeft}px`);
    }
  }, [isSearchVisible]);

  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  // Re-position overlay horizontally when expanded state changes
  useEffect(() => {
    const id = window.setTimeout(() => updateOverlayPosition(), 30);
    return () => window.clearTimeout(id);
  }, [isSearchVisible, updateOverlayPosition]);

  const handleToggleSearch = () => {
    if (isSearchVisible) {
      // Clear input immediately
      if (searchRef.current) {
        try { (searchRef.current as HTMLIonSearchbarElement).value = ''; } catch { }
      }
      setSearch('');
      try {
        const s = searchRef.current as HTMLIonSearchbarElement | null;
        const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
        if (getter) {
          getter().then((inputEl: HTMLInputElement) => {
            try { inputEl.blur(); } catch { }
          }).catch(() => { });
        }
      } catch { }
      // Start closing animation
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      setTimeout(() => {
        setIsSearchHidden(true);
        setIsSearchClosing(false);
      }, 220); // Match animation duration
    } else {
      setIsSearchHidden(false);
      setIsSearchVisible(true);

      // Focus input after expansion animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updateOverlayPosition();
          try {
            const s = searchRef.current as HTMLIonSearchbarElement | null;
            const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
            if (getter) {
              getter().then((inputEl: HTMLInputElement) => {
                try { inputEl.focus(); } catch { }
              }).catch(() => { });
            }
          } catch { }
        });
      });
    }
  };

  const handleAddPeople = () => {
    loadMembers(true).catch(() => { });
  };

  const filteredMembers = members.filter(member => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return member.name.toLowerCase().includes(query)
      || member.email.toLowerCase().includes(query)
      || member.phone.toLowerCase().includes(query);
  });

  const openMemberChat = useCallback((member: TeamMember) => {
    navigate('/team/messages', {
      state: {
        newChat: {
          id: member.id,
          name: member.name,
          email: member.email,
          phone: member.phone,
          avatarUrl: member.avatarUrl,
        },
      },
    });
  }, [navigate]);

  return (
    <IonPage className={contactsStyles.page}>
      <BasePageWithHeader
        ref={baseHeaderRef}
        title={t('pages.teamMessaging.MembersPage.members')}
        toolbarClassName={contactsStyles.toolbar}
        smallTitleClassName={contactsStyles.smallTitle}
        titleHeaderClassName={contactsStyles.titleHeader}
        startButtons={
          <>
            <IonButton fill="clear" onClick={() => ionRouter.goBack()} aria-label={t('pages.teamMessaging.MembersPage.back')}>
              <IonIcon icon={chevronBackOutline} />
            </IonButton>
            <IonButton fill="clear" onClick={() => navigate('/dashboard', { replace: true })} aria-label={t('pages.teamMessaging.MembersPage.appHome')}>
              <IonIcon icon={homeOutline} />
            </IonButton>
          </>
        }
        endButtons={
          <IonButton
            shape="round"
            className={contactsStyles.searchButton}
            onClick={handleToggleSearch}
          >
            <IonIcon icon={isSearchVisible ? closeOutline : searchOutline} />
          </IonButton>
        }
      >
        <IonContent scrollY={false} className={contactsStyles.content}>
          <IonGrid
            ref={gridRef}
            className={contactsStyles.contactsContainer}
            onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
          >
            <div ref={titleCardRef} className={contactsStyles.titleCardWrapper}>
              <TitleCard
                ref={titleCardAnimationRef}
                title={t('pages.teamMessaging.MembersPage.members')}
                cardClassName={contactsStyles.contactsTitleCard}
                titleContainerClassName={contactsStyles.titleContainer}
                titleClassName={contactsStyles.contactsTitle}
              />
            </div>

            {loading ? (
              <div className={contactsStyles.loadingState}>
                <IonSpinner name="bubbles" color="primary" />
                <span>{t('pages.teamMessaging.MembersPage.loadingStaffMembers')}</span>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className={contactsStyles.emptyState}>
                <IonIcon icon={peopleOutline} />
                <span>{search ? t('pages.teamMessaging.MembersPage.noMembersMatchYourSearch') : t('pages.teamMessaging.MembersPage.noStaffMembersFound')}</span>
              </div>
            ) : (
              <IonList className={contactsStyles.contactList}>
                {filteredMembers.map((member) => (
                  <IonItemSliding key={member.id}>
                    <IonItemOptions side="start">
                      {member.phone && (
                        <IonItemOption color="primary" expandable onClick={(e) => { e.preventDefault(); window.location.href = `tel:${member.phone}`; }} className={contactsStyles.smallOptionText}>
                          <IonIcon slot="bottom" icon={callOutline} />{t('pages.teamMessaging.MembersPage.call')}
                        </IonItemOption>
                      )}
                      {member.email && (
                        <IonItemOption color="secondary" expandable onClick={(e) => { e.preventDefault(); window.location.href = `mailto:${member.email}`; }} className={contactsStyles.smallOptionText}>
                          <IonIcon slot="bottom" icon={mailOutline} />{t('pages.teamMessaging.MembersPage.email')}
                        </IonItemOption>
                      )}
                    </IonItemOptions>

                    <IonItem button detail={false} className={contactsStyles.solidItem} role="listitem">
                      <IonAvatar slot="start" className={contactsStyles.avatar}>
                        <img
                          src={member.avatarUrl || getDefaultAvatar(member.name)}
                          alt={member.name}
                          onError={(e) => {
                            const imgElement = e.target as HTMLImageElement;
                            imgElement.src = getDefaultAvatar(member.name);
                          }}
                        />
                      </IonAvatar>

                      <IonLabel>
                        <IonText className={contactsStyles.contactName}>
                          <h3>{member.name}</h3>
                        </IonText>
                        <IonText className={contactsStyles.lastMessage}>
                          <p>{getMemberStatus(member)}</p>
                        </IonText>
                      </IonLabel>

                      <IonChip className={contactsStyles.memberChip} slot="end">
                        <IonLabel>{t('pages.teamMessaging.MembersPage.staff')}</IonLabel>
                      </IonChip>

                      <IonIcon
                        slot="end"
                        icon={swapHorizontal}
                        color="medium"
                        size="small"
                        className={contactsStyles.slideIndicator}
                        aria-label={t('pages.teamMessaging.MembersPage.swipeForOptions')}
                      />
                    </IonItem>

                    <IonItemOptions side="end">
                      <IonItemOption color="secondary" expandable onClick={(e) => { e.preventDefault(); openMemberChat(member); }} className={contactsStyles.smallOptionText}>
                        <IonIcon slot="bottom" icon={mailOutline} />{t('pages.teamMessaging.MembersPage.message')}
                      </IonItemOption>
                    </IonItemOptions>
                  </IonItemSliding>
                ))}
              </IonList>
            )}
          </IonGrid>

          {/* Keep FAB and search overlay outside of IonContent so they stay fixed to the viewport */}
          <IonFab className={contactsStyles.calendarFab}>
            <IonFabButton
              size='small'
              onClick={() => {
                if (isSearchVisible) {
                  handleToggleSearch();
                } else {
                  handleAddPeople();
                }
              }}
              aria-label={isSearchVisible ? t('pages.teamMessaging.MembersPage.closeSearch') : t('pages.teamMessaging.MembersPage.refreshStaffMembers')}
            >
              <IonIcon icon={isSearchVisible ? closeOutline : refreshOutline} />
            </IonFabButton>
          </IonFab>

          {/* Non-invasive overlay: centered pop-out */}
          <IonFab ref={el => { overlayRef.current = el as unknown as HTMLElement; }} className={`${contactsStyles.searchMorph} ${isSearchVisible ? contactsStyles.searchMorphExpanded : ''} ${isSearchClosing ? contactsStyles.searchMorphClosing : ''} ${isSearchHidden ? contactsStyles.hidden : ''}`} aria-hidden={isSearchVisible ? 'false' : 'true'}>
            <IonSearchbar
              ref={el => { searchRef.current = el as unknown as HTMLIonSearchbarElement | null; }}
              value={search}
              debounce={50}
              onIonFocus={() => {
                requestAnimationFrame(() => updateOverlayPosition());
              }}
              onIonInput={e => setSearch(e.detail.value ?? '')}
              onIonClear={() => setSearch('')}
              placeholder={t('pages.teamMessaging.MembersPage.searchMembers')}
              animated
              showCancelButton="never"
              showClearButton={search ? 'always' : 'never'}
              className={`${contactsStyles.searchBar} ${contactsStyles.floatingSearchBarInline} ${isSearchVisible ? contactsStyles.floatingSearchBarInlineExpanded : ''}`}
            />
          </IonFab>
        </IonContent>
      </BasePageWithHeader>
    </IonPage>
  );
};

export default MembersPage;
